/**
 * TemplateWorkspaceManager
 *
 * UI component for managing multiple templates in the template viewer.
 * Uses useTemplateWorkspace hook for state management.
 */

import React, { useState } from "react";
import { useTemplateWorkspace } from "@/hooks/useTemplateWorkspace";
import { X, Plus, FileJson } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TemplateCompactSelector } from "./TemplateSelector";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const TemplateWorkspaceManager: React.FC = () => {
  const [showAddTemplate, setShowAddTemplate] = useState(false);

  // Use the workspace hook for all state management
  const {
    pages,
    currentPageId,
    currentTemplateId,
    addTemplate: hookAddTemplate,
    removeTemplate: hookRemoveTemplate,
    switchTemplate,
    availableTemplates,
  } = useTemplateWorkspace();

  // Wrapper to close the add template UI
  const addTemplate = (templateId: string) => {
    hookAddTemplate(templateId);
    setShowAddTemplate(false);
  };

  // Handle page click
  const handlePageClick = (templateId: string) => {
    switchTemplate(templateId);
  };

  // Full sidebar view
  return (
    <div className="h-full flex flex-col bg-background border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="font-semibold text-sm text-foreground mb-1">Template Browser</h2>
        <p className="text-xs text-muted-foreground">
          {pages.length} template{pages.length !== 1 ? 's' : ''} loaded
        </p>
      </div>

      {/* Template Pages */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {pages.map((page) => {
          const templateId = page.id.replace('template-', '');
          const isActive = page.id === currentPageId;

          return (
            <div
              key={page.id}
              className={cn(
                "group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )}
              onClick={() => handlePageClick(templateId)}
            >
              <FileJson className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-sm truncate">{page.name}</span>
              {pages.length > 1 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          hookRemoveTemplate(templateId);
                        }}
                        className={cn(
                          "opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-background/20",
                          isActive && "hover:bg-primary-foreground/20"
                        )}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Remove from workspace</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Template Button */}
      <div className="p-2 border-t border-border">
        {showAddTemplate ? (
          <div className="space-y-2">
            <TemplateCompactSelector
              selectedTemplateId={null}
              onTemplateSelect={addTemplate}
              className="w-full"
              placeholder="Select template to add..."
              availableTemplates={availableTemplates}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddTemplate(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddTemplate(true)}
            className="w-full gap-2"
            disabled={availableTemplates.length === 0}
          >
            <Plus className="w-4 h-4" />
            Add Template
          </Button>
        )}
      </div>
    </div>
  );
};
